import React, { useCallback, useState, useEffect } from 'react';
import {
  getUsuarios,
  desactivarUsuario,
  activarUsuario,
  eliminarUsuario,
  resetTemporaryPassword,
  Usuario,
} from '../services/usuarioService';
import UsuarioForm from './UsuarioForm';
import { hasUserManagementPermission } from '../services/authService';
import { Badge, Button, IconButton, EmptyState, LoadingState } from './ui';
import {
  PencilIcon,
  PlusIcon,
  ArrowLeftIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  ArrowUturnLeftIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface UsuariosListProps { onCreateNew?: () => void }

const mensajeDeError = (err: any, alternativa: string): string =>
  err?.response?.data?.message || err?.message || alternativa;

const UsuariosList: React.FC<UsuariosListProps> = ({ onCreateNew }) => {
  const puedeGestionar = hasUserManagementPermission();

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState<Usuario | undefined>(undefined);

  const fetchUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      setUsuarios(await getUsuarios());
      setError(null);
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo cargar la lista de usuarios.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (puedeGestionar) fetchUsuarios();
  }, [puedeGestionar, fetchUsuarios]);

  // Las cuentas no se borran: se desactivan, para conservar la referencia en los
  // análisis que la persona generó o finalizó. La acción es reversible.
  const handleCambiarEstado = async (usuario: Usuario) => {
    if (!usuario.id) return;
    const nombre = `${usuario.nombre} ${usuario.apellido || ''}`.trim();
    const desactivando = !!usuario.estado;

    const confirmacion = desactivando
      ? `¿Confirma que desea desactivar a ${nombre}? No podrá iniciar sesión, pero su historial se conserva y puede volver a activarlo cuando quiera.`
      : `¿Confirma que desea activar nuevamente a ${nombre}?`;
    if (!window.confirm(confirmacion)) return;

    setError(null);
    setAviso(null);
    try {
      if (desactivando) {
        await desactivarUsuario(usuario.id);
      } else {
        await activarUsuario(usuario.id);
      }
      setUsuarios(current =>
        current.map(item => (item.id === usuario.id ? { ...item, estado: desactivando ? 0 : 1 } : item))
      );
      setAviso(desactivando ? `${nombre} quedó desactivado.` : `${nombre} volvió a estar activo.`);
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo cambiar el estado del usuario.'));
    }
  };

  // Eliminar es distinto de desactivar: la cuenta pasa a estado 2 y desaparece
  // de la lista. El registro queda en la base, así los análisis que la persona
  // generó o finalizó no pierden la referencia a su autor.
  const handleEliminar = async (usuario: Usuario) => {
    if (!usuario.id) return;
    const nombre = `${usuario.nombre} ${usuario.apellido || ''}`.trim();
    const confirmacion =
      `¿Confirma que desea eliminar a ${nombre}? Dejará de aparecer en la lista y no podrá iniciar sesión.` +
      ' Los análisis que generó o finalizó conservan su nombre.';
    if (!window.confirm(confirmacion)) return;

    setError(null);
    setAviso(null);
    try {
      await eliminarUsuario(usuario.id);
      setUsuarios(current => current.filter(item => item.id !== usuario.id));
      setAviso(`${nombre} fue eliminado.`);
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo eliminar el usuario.'));
    }
  };

  const handleEdit = (usuario: Usuario) => {
    setEditingUsuario(usuario);
    setIsFormVisible(true);
  };

  const handleCreateNew = () => {
    if (onCreateNew) {
      onCreateNew();
      return;
    }
    setEditingUsuario(undefined);
    setIsFormVisible(true);
  };

  const handleResetPassword = async (usuario: Usuario) => {
    if (!usuario.id) return;
    const confirmacion =
      `Se generará una contraseña nueva para ${usuario.nom_usuario} y se enviará por correo a ${usuario.email}.\n\n` +
      'La contraseña actual dejará de funcionar. ¿Desea continuar?';
    if (!window.confirm(confirmacion)) return;

    setError(null);
    setAviso(null);
    try {
      const resultado = await resetTemporaryPassword(usuario.id);
      setUsuarios(current =>
        current.map(item => (item.id === usuario.id ? { ...item, estado_pass: 0, passwordPendiente: true } : item))
      );
      setAviso(
        resultado.emailSent
          ? `Se envió la contraseña nueva a ${usuario.email}.`
          : `Se generó la contraseña ${resultado.generatedPass ?? ''}, pero no se pudo enviar el correo. Entréguela usted mismo.`
      );
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo generar una contraseña nueva.'));
    }
  };

  const handleFormCancel = () => {
    setIsFormVisible(false);
    setEditingUsuario(undefined);
  };

  const handleUsuarioSaved = () => {
    setIsFormVisible(false);
    setEditingUsuario(undefined);
    fetchUsuarios();
  };

  const formatTipoUsuario = (tipo: string | undefined): string => {
    if (!tipo) return '-';
    const tiposMap: { [key: string]: string } = {
      cliente: 'Cliente',
      laboratorio: 'Laboratorio',
      administrador: 'Administrador',
    };
    return tiposMap[tipo] || tipo;
  };

  // Habilitado en verde, o el símbolo de prohibido en rojo. Ambos del mismo tamaño.
  const PermisoIndicator: React.FC<{ value: boolean | number | undefined }> = ({ value }) =>
    value ? (
      <CheckCircleIcon className="h-5 w-5 text-green-600 mx-auto" aria-label="Habilitado" />
    ) : (
      <NoSymbolIcon className="h-5 w-5 text-red-500 mx-auto" aria-label="Deshabilitado" />
    );

  if (!puedeGestionar) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-center">
        <strong>Acceso denegado:</strong> no cuenta con el permiso de gestión de usuarios.
      </div>
    );
  }

  if (isFormVisible) {
    return (
      <div>
        <Button
          variant="secondary"
          size="sm"
          icon={<ArrowLeftIcon className="h-4 w-4" />}
          onClick={handleFormCancel}
          className="mb-4"
        >
          Volver a la lista
        </Button>
        <UsuarioForm usuario={editingUsuario} onUsuarioSaved={handleUsuarioSaved} onCancel={handleFormCancel} />
      </div>
    );
  }

  if (loading && usuarios.length === 0) {
    return <LoadingState label="Cargando usuarios..." />;
  }

  const columnas = [
    { label: '#', className: 'w-14' },
    { label: 'Usuario', className: 'min-w-[9rem]' },
    { label: 'Nombre', className: 'min-w-[8rem]' },
    { label: 'Apellido', className: 'min-w-[8rem]' },
    { label: 'Email', className: 'min-w-[14rem]' },
    { label: 'Estado', className: 'min-w-[6rem]' },
    { label: 'Tipo', className: 'min-w-[8rem]' },
  ];

  const permisos: { label: string; campo: keyof Usuario }[] = [
    { label: 'Búsqueda', campo: 'p_busqueda' },
    { label: 'Carga', campo: 'p_carga' },
    { label: 'Modificar', campo: 'p_modif_analisis' },
    { label: 'Crear análisis', campo: 'p_nuevo_analisis' },
    { label: 'Usuarios', campo: 'p_g_usuarios' },
    { label: 'Ensayos', campo: 'p_g_ensayos' },
  ];

  return (
    <div className="w-full p-4">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h2 className="text-2xl font-semibold text-slate-900">Lista de usuarios</h2>
        <Button icon={<PlusIcon className="h-4 w-4" />} onClick={handleCreateNew}>
          Nuevo usuario
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
          {error}
        </div>
      )}

      {aviso && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md mb-4" role="status">
          {aviso}
        </div>
      )}

      {usuarios.length === 0 ? (
        <EmptyState title="No hay usuarios registrados" />
      ) : (
        <div className="scroll-siempre rounded-lg border border-slate-200 bg-white max-h-[32rem]">
          <table className="min-w-full border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-100">
                {columnas.map(columna => (
                  <th
                    key={columna.label}
                    rowSpan={2}
                    className={`px-4 py-2 border-b border-slate-200 bg-slate-100 text-left text-sm font-medium text-slate-600 ${columna.className}`}
                  >
                    {columna.label}
                  </th>
                ))}
                <th
                  colSpan={permisos.length}
                  className="px-4 py-2 border-b border-slate-200 bg-slate-100 text-center text-sm font-medium text-slate-600"
                >
                  Permisos
                </th>
                <th rowSpan={2} className="px-4 py-2 border-b border-slate-200 bg-slate-100 text-left text-sm font-medium text-slate-600 min-w-[11rem]">
                  Contraseña
                </th>
                <th rowSpan={2} className="px-4 py-2 border-b border-slate-200 bg-slate-100 text-left text-sm font-medium text-slate-600 min-w-[7rem]">
                  Acciones
                </th>
              </tr>
              <tr className="bg-slate-100">
                {permisos.map(permiso => (
                  <th
                    key={permiso.campo as string}
                    className="px-2 py-1 border-b border-slate-200 bg-slate-100 text-xs font-medium text-slate-500 whitespace-nowrap"
                  >
                    {permiso.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usuarios.map((usuario, indice) => (
                <tr key={usuario.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 border-b border-slate-100 text-sm text-slate-500">{indice + 1}</td>
                  <td className="px-4 py-2 border-b border-slate-100 text-sm text-slate-700">{usuario.nom_usuario || '-'}</td>
                  <td className="px-4 py-2 border-b border-slate-100 text-sm text-slate-700">{usuario.nombre}</td>
                  <td className="px-4 py-2 border-b border-slate-100 text-sm text-slate-700">{usuario.apellido || '-'}</td>
                  <td className="px-4 py-2 border-b border-slate-100 text-sm text-slate-700">{usuario.email}</td>
                  <td className="px-4 py-2 border-b border-slate-100">
                    <Badge tone={usuario.estado ? 'success' : 'danger'}>{usuario.estado ? 'Activo' : 'Inactivo'}</Badge>
                  </td>
                  <td className="px-4 py-2 border-b border-slate-100 text-sm text-slate-700">
                    {formatTipoUsuario(usuario.tipo_usuario || usuario.tabla_grupo?.replace(/s$/, ''))}
                  </td>
                  {permisos.map(permiso => (
                    <td key={permiso.campo as string} className="px-2 py-2 border-b border-slate-100 text-center">
                      <PermisoIndicator value={usuario[permiso.campo] as boolean | number | undefined} />
                    </td>
                  ))}
                  <td className="px-4 py-2 border-b border-slate-100 text-sm">
                    {usuario.estado_pass === 1 ? (
                      <span className="text-slate-500">Definida por el usuario</span>
                    ) : (
                      <span className="text-amber-700">Temporal, pendiente de cambio</span>
                    )}
                    <button
                      type="button"
                      className="block mt-1 text-xs text-brand-700 hover:underline"
                      onClick={() => handleResetPassword(usuario)}
                    >
                      Generar nueva clave y enviar por correo
                    </button>
                  </td>
                  <td className="px-4 py-2 border-b border-slate-100">
                    <div className="flex gap-1">
                      <IconButton
                        label="Editar usuario"
                        tone="brand"
                        icon={<PencilIcon className="h-4 w-4" />}
                        onClick={() => handleEdit(usuario)}
                      />
                      <IconButton
                        label={usuario.estado ? 'Desactivar usuario' : 'Activar usuario'}
                        tone={usuario.estado ? 'danger' : 'brand'}
                        icon={
                          usuario.estado ? (
                            <NoSymbolIcon className="h-4 w-4" />
                          ) : (
                            <ArrowUturnLeftIcon className="h-4 w-4" />
                          )
                        }
                        onClick={() => handleCambiarEstado(usuario)}
                      />
                      <IconButton
                        label="Eliminar usuario"
                        tone="danger"
                        icon={<TrashIcon className="h-4 w-4" />}
                        onClick={() => handleEliminar(usuario)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UsuariosList;
